import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { fetchBuilderDataFromModel } from '../../../common/utils';
import {
  Button,
  type ButtonProps,
} from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import { BUTTON_VARIANTS } from '../../utils/constants';
import withBuilderDefaults from '../../utils/withBuilderDefaults';

export interface FooterHookBuilderData {
  headline: string;
  content: string;
  imageSrc: string;
}

export interface FooterHookLandingSearchProps {
  urlSlug?: string;
  modelName?: string;
  showCallToAction?: boolean;
  callToAction?: ButtonProps & { text: string };
  shadowVariant?: boolean;
  backgroundColor?: string;
  shadowOptions?: {
    shadowColor: string;
    shadowImage: string;
  };
}

const BUILDER_ASSET_BASE =
  'https://cdn.builder.io/api/v1/image/assets%2Fe7fb3d1e4da14573bd2a1edb7bfee5f1%2F';

// Design content, used for whatever the Builder entry leaves empty. The copy is
// shared across the landing pages, the background image is per asset type.
const FOOTER_HOOK_COPY = {
  headline: 'Speed up production with a Storyblocks subscription',
  content:
    'Unlimited stock downloads, 5 million+ pre-licensed assets and access to time-saving tools and features',
};

const FOOTER_HOOK_IMAGES: Record<string, string> = {
  '/video': `${BUILDER_ASSET_BASE}e9d02e05795e47cab8da0cb025f6a24e`,
  '/audio': `${BUILDER_ASSET_BASE}e6b228624f9c48e3b473ffa50e03ef5d`,
  '/images': `${BUILDER_ASSET_BASE}3d43c36cbad84f60ab9f8be78186129d`,
};

// A slug the design has no image for has nothing to put behind the copy, so the
// section stays hidden there rather than rendering an empty band.
const footerHookDefaults = (urlSlug?: string): FooterHookBuilderData => ({
  ...FOOTER_HOOK_COPY,
  imageSrc: (urlSlug && FOOTER_HOOK_IMAGES[urlSlug]) || '',
});

const DEFAULT_CALL_TO_ACTION: ButtonProps & { text: string } = {
  text: 'Explore Plans',
  href: '/pricing',
  variant: BUTTON_VARIANTS[5],
  size: 'default',
  target: '_self',
  rel: '',
};

const FooterHookLandingSearch = ({
  urlSlug,
  modelName = 'landing-search-page',
  showCallToAction = true,
  callToAction = DEFAULT_CALL_TO_ACTION,
  shadowVariant = false,
  backgroundColor,
  shadowOptions,
}: FooterHookLandingSearchProps) => {
  const { shadowColor, shadowImage } = shadowOptions || {};
  const contentClass = twMerge(
    'relative mx-auto max-w-screen-xl',
    shadowVariant && 'left-6'
  );

  const [builderData, setBuilderData] = useState<FooterHookBuilderData>(null);

  useEffect(() => {
    const builderOptions = {
      query: {
        'data.urlSlug': urlSlug,
      },
      options: {
        fields: 'data',
      },
    };
    const fetchBuilderData = async () => {
      const data = await fetchBuilderDataFromModel(modelName, builderOptions);
      if (data && data.footerHook) {
        setBuilderData({
          headline: data.footerHook.headline,
          content: data.footerHook.content,
          imageSrc: data.footerHook.imageSrc,
        });
      }
    };
    if (urlSlug) {
      fetchBuilderData();
    }
  }, [urlSlug, modelName]);

  const footerHook = withBuilderDefaults(
    builderData,
    footerHookDefaults(urlSlug)
  );

  if (!footerHook.imageSrc) {
    return null;
  }

  return (
    <div
      className={twMerge(
        'px-6 py-16 md:px-12 md:py-24 lg:p-24',
        shadowVariant && 'pr-12 md:pr-20 lg:pr-32'
      )}
      style={{
        backgroundImage: `url(${footerHook.imageSrc})`,
        backgroundSize: 'cover',
      }}
    >
      <div className={contentClass}>
        {shadowVariant ? (
          <div className="absolute -inset-0">
            <div
              className="absolute w-full h-full -left-6 top-6 rounded-3xl"
              style={{
                backgroundColor: shadowColor,
                backgroundImage: shadowImage
                  ? `url(${shadowImage})`
                  : undefined,
                backgroundSize: shadowImage && 'cover',
              }}
              role="presentation"
            />
          </div>
        ) : null}
        <div
          className="relative px-6 py-12 md:p-12 md:gap-4 lg:p-16 rounded-3xl"
          style={{
            backgroundColor: backgroundColor ?? 'var(--color-white, #ffffff)',
          }}
        >
          <div className="flex flex-col gap-4 text-gray-900 lg:flex-row lg:gap-8">
            <Text
              fontFamily="polysans"
              fontSize="4xl"
              fontWeight="semibold"
              className="m-0 flex-1"
              asChild
            >
              <h2>{footerHook.headline}</h2>
            </Text>
            <div className="flex-1 lg:rounded-3xl">
              <Text
                fontFamily="polysans"
                className="m-0 flex-1"
                fontSize="lg"
                fontWeight="regular"
              >
                {footerHook.content}
              </Text>

              {showCallToAction ? (
                <Button
                  variant={callToAction.variant}
                  className="mt-8"
                  size="large"
                  href={callToAction.href}
                  target={callToAction.target}
                >
                  {callToAction.text}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterHookLandingSearch;
